import { MBMData, PricingConfig, PricingFactor, PricingItem, ServiceValue } from 'app/types/pricing'

export const serviceElements = {
  KCSM_CARE_MBM: 'KCSM_CARE_MBM',
  KCSM_SR_PKG_INCL: 'KCSM_SR_PKG_INCL',
  KCSM_SR_PKG_EXCL: 'KCSM_SR_PKG_EXCL',
  KCSM_DISTANCE_RANGE: 'KCSM_DISTANCE_RANGE',
  KCSM_SR_ONDEMAND: 'KCSM_SR_ONDEMAND',
  KCSM_CARE_INSPECTION: 'KCSM_CARE_INSPECTION',
  KCSM_CO: 'KCSM_CO',
  KCSM_NBR_CO_YEAR: 'KCSM_NBR_CO_YEAR',
  KCSM_ASSET_MANAGEMENT: 'KCSM_ASSET_MANAGEMENT',
  KCSM_INSP_SCE_TEST: 'KCSM_INSP_SCE_TEST',
  KCSM_TRAINING: 'KCSM_TRAINING',
  KCSM_CLEANING: 'KCSM_CLEANING',
  KCSM_CONTRACT_MANAGEMENT: 'KCSM_CONTRACT_MANAGEMENT'
}

export const subfactors = {
  numberOfDoors: 'numberOfDoors',
  equipmentAge: 'equipmentAge',
  equipmentUsage: 'equipmentUsage',
  operationalEnvironment: 'operationalEnvironment',
  technicalPlatform: 'technicalPlatform',
  additionalDoors: 'additionalDoors',
  hourlyRate: 'hourlyRate',
  averageTravelTime: 'averageTravelTime',
  unrealisedProductivity: 'unrealisedProductivity',
  travelTime: 'travelTime',
  speed: 'speed',
  length: 'length',
  rise: 'rise'
}

export const typeCheckFactors = {
  number: 'number',
  string: 'string',
  mvType: 'mv_type',
  value: 'value',
  object: 'object',
  percentage: 'percentage'
}

export const noPriceChoices = {
  NA: 'NA',
  ZERO: '0',
  NO: 'N'
}

export const specialPriceChoices = {
  onDemandFive: '5',
  onDemandSix: '6',
  onDemandSeven: '7',
  onCareInspectionThree: '3'
}

export const profileEntry = {
  equipmentCategory: 'equipmentCategory',
  equipmentType: 'equipmentType'
}

export const equipmentCategory = {
  elevator: 'elevator',
  escalator: 'escalator'
}

export const escalatorType = {
  autowalk: 'autowalk',
  escalator: 'escalator'
}

export const mandatoryPricingKeys = [
  'speed',
  'load',
  'unrealisedProductivity',
  'KCSM_CARE_MBM',
  'segment',
  'opportunityCategory',
  'fundingSector',
  'willingnessToPay',
  'numberOfEquipment',
  'numberOfDrives',
  'technicalPlatform',
  'equipmentAge',
  'operationalEnvironment',
  'escalatorOperationalEnvironment',
  'rise'
]

export const getProfileKeyMapping = (profileKey: string): string => {
  const keymap: Record<string, string> = {
    numberOfEquipment: 'number_of_equipment_elevators',
    willingnessToPay: 'willingnessToPay',
    equipmentUsage: 'usage',
    equipmentAge: 'age'
  }
  return keymap[profileKey] || profileKey
}

export const getProfileChoice = (profile: Record<string, any>, factor: string): number | string => {
  const keymap = getProfileKeyMapping(factor)
  return profile[keymap] || 0
}

export const getMBMFactors = (mbmData: MBMData) =>
  mbmData[serviceElements.KCSM_CARE_MBM]?.find((item: any) => item.choice === '0')

export const getDefaultPricingFactor = (key: string, choice: string | number): PricingFactor => ({
  key,
  price: 0,
  value: '0',
  choice
})

export const getRangeValue = (choice: number, factorData: PricingItem[]) =>
  factorData.find(item => {
    const [start, end] = item?.range ?? []
    return typeof start === typeCheckFactors.number && typeof end === typeCheckFactors.number
      ? Number(choice) >= start && Number(choice) <= end
      : false
  })

/**
 * get mv object after matching choice
 * [["Z1",{ "value": 0, "mv_type": "percentage"}],["Z2",{ "value": 10, "mv_type": "percentage"}]]
 */
export const getMVinArrayWithChoice = (mbmData: [string, ServiceValue][], choice: string) => {
  const [, mvObject] = mbmData.find(([ch]) => ch === choice) || []
  return mvObject || undefined
}

/**
 * get mv object value as string or number
 * { "value": 10, "mv_type": "percentage"}
 */
export const getMVTypeValue = (entry: any, basePrice: number, isDirectValue: boolean = false): number | string => {
  const isPercentage = entry.mv_type === typeCheckFactors.percentage
  return isDirectValue
    ? ((isPercentage ? `${entry.value}%` : String(entry.value)) as string)
    : ((isPercentage ? basePrice * (entry.value / 100) : entry.value) as number)
}

export const isValidProfileEntry = (value: any): boolean =>
  Array.isArray(value) ||
  (value !== undefined &&
    value !== null &&
    value !== '' &&
    value !== 'N' &&
    value !== 'NA' &&
    value !== '0' &&
    value !== 0)

export const elevatorPricingConfig: PricingConfig[] = [
  {
    title: 'Travel Time Price',
    priceKey: 'travelTime',
    isDependent: false,
    servicesIncluded: ['KCSM_DISTANCE_RANGE'],
    subFactorsIncluded: ['baseUsage', 'averageTravelTime', 'hourlyRate'],
    pricingDependents: ['travelTime'],
    pricingFactors: ['hourlyRate', 'averageTravelTime', 'KCSM_DISTANCE_RANGE'],
    calculationFormula: `hourlyRate * averageTravelTime * (1+KCSM_DISTANCE_RANGE)`
  },
  {
    title: 'Initial MBM Door Price',
    priceKey: 'initMBMDoorPrice',
    isDependent: false,
    servicesIncluded: ['KCSM_CARE_MBM'],
    subFactorsIncluded: ['numberOfDoors', 'additionalDoors'],
    calculationFormula: `sum of (numberOfDoors + additionalDoors)`
  },
  {
    title: 'Regular MBM Price',
    priceKey: 'regularMBMPrice',
    dependentPricing: 'initMBMDoorPrice',
    isDependent: true,
    servicesIncluded: ['KCSM_CARE_MBM'],
    subFactorsIncluded: ['equipmentUsage', 'technicalPlatform', 'equipmentAge', 'operationalEnvironment'],
    calculatedPriceKeys: ['initMBMDoorPrice', 'travelTime'],
    calculationFormula: `sum of (initMBMDoorPrice + travelTime + equipmentUsage + technicalPlatform + equipmentAge + operationalEnvironment)`
  },
  {
    title: 'Janitor Care Price',
    priceKey: 'KCSM_CARE_JANITOR',
    isDependent: false,
    servicesIncluded: ['KCSM_CARE_JANITOR']
  },
  {
    title: 'Number of Janitor Visit Price',
    priceKey: 'KCSM_NUMBER_JANITOR_VISIT',
    isDependent: true,
    dependentPricing: 'KCSM_CARE_JANITOR',
    servicesIncluded: ['KCSM_NUMBER_JANITOR_VISIT']
  },
  {
    title: 'MBM Price',
    priceKey: 'MBMPrice',
    dependentPricing: 'regularMBMPrice',
    isDependent: true,
    servicesIncluded: [
      'KCSM_CARE_VISIT',
      'KCSM_VISIT_FREQUENCY_YEARLY',
      'KCSM_AGREED_CUST_VISITS_YEARLY',
      'KCSM_SN_STANDARD',
      'KCSM_REMOTE_MAINT',
      'KCSM_MAINT_HOURS',
      'KCSM_MAINT_PRE_BOOK',
      'KCSM_PREFERRED_TECHNICIAN',
      'KCSM_PRESENCE',
      'KCSM_FIRE_PACKAGE',
      'KCSM_AIR_CONDITION_MAINT',
      'KCSM_AIR_PURIFIER',
      'KCSM_ACCESS_MAINT',
      'KCSM_DCS_MAINT'
    ],
    calculatedPriceKeys: ['KCSM_CARE_JANITOR', 'KCSM_NUMBER_JANITOR_VISIT', 'regularMBMPrice']
  },
  {
    title: 'Initial Call Out Door Price (KCSM_CO/KCSM_NBR_CO_YEAR)',
    priceKey: 'initCODoorPrice',
    isDependent: false,
    servicesIncluded: ['KCSM_CO'],
    subFactorsIncluded: ['numberOfDoors'],
    calculationFormula: `KCSM_CO === 0 ? serviceIncluded will change to KCSM_NBR_CO_YEAR and relevant factors are considered `
  },
  {
    title: 'Factor Call Out Price',
    priceKey: 'factorCOPrice',
    isDependent: true,
    dependentPricing: 'initCODoorPrice',
    servicesIncluded: ['KCSM_CO'],
    subFactorsIncluded: ['equipmentUsage', 'technicalPlatform', 'equipmentAge', 'operationalEnvironment'],
    calculationFormula: `KCSM_CO === 0 ? serviceIncluded will change to KCSM_NBR_CO_YEAR and relevant factors are considered `
  },
  {
    title: 'Regular Call Out Price',
    priceKey: 'regularCOPrice',
    isDependent: true,
    dependentPricing: 'initCODoorPrice',
    servicesIncluded: ['KCSM_CCC'],
    calculatedPriceKeys: ['initCODoorPrice', 'factorCOPrice', 'travelTime']
  },
  {
    title: 'Call Out Price',
    priceKey: 'COPriceOne',
    isDependent: true,
    dependentPricing: 'regularCOPrice',
    servicesIncluded: ['KCSM_SERVICE_HOURS', 'KCSM_CO_HOURS']
  },
  {
    title: 'Misuse Price',
    priceKey: 'misusePrice',
    isDependent: true,
    dependentPricing: 'regularCOPrice',
    servicesIncluded: ['KCSM_CARE_MISUSE'],
    calculatedPriceKeys: ['travelTime']
  },
  {
    title: 'Call Out Subtotal One',
    priceKey: 'callOutSubTotalOne',
    isDependent: false,
    calculatedPriceKeys: ['regularCOPrice', 'misusePrice', 'COPriceOne']
  },
  {
    title: 'SN Immediate Price',
    priceKey: 'snImmediatePrice',
    isDependent: false,
    servicesIncluded: ['KCSM_SN_IMMEDIATE']
  },
  {
    title: 'All Call Out Price',
    priceKey: 'allCallOutPrice',
    isDependent: true,
    dependentPricing: 'callOutSubTotalOne',
    calculatedPriceKeys: ['regularCOPrice', 'COPriceOne', 'misusePrice', 'snImmediatePrice'],
    servicesIncluded: ['KCSM_REMOTE_BREAKDOWN', 'KCO_PC_RESP', 'KCO_PC_RESP_A', 'KCSM_CARE_CALL_OUT_FEE']
  },
  {
    title: 'Entrapment Rescue Price',
    priceKey: 'KCSM_RESCUE_SERVICE',
    isDependent: true,
    dependentPricing: 'initMBMDoorPrice',
    calculatedPriceKeys: [],
    servicesIncluded: ['KCSM_RESCUE_SERVICE']
  },
  {
    title: 'All Rescue Service Price',
    priceKey: 'allRescueServicePrice',
    isDependent: true,
    dependentPricing: 'KCSM_RESCUE_SERVICE',
    servicesIncluded: [
      'KCSM_RESCUE_COST',
      'KCO_PC_RESP_E',
      'KCO_PC_RESP_E_A',
      'KCSM_VOICE_L',
      'KCSM_VOICE_LINK_INST',
      'KCSM_UPS'
    ],
    calculatedPriceKeys: ['KCSM_RESCUE_SERVICE']
  },
  {
    title: 'Subtotal 1',
    priceKey: 'subtotal1',
    isDependent: false,
    servicesIncluded: [],
    calculatedPriceKeys: ['allRescueServicePrice', 'allCallOutPrice', 'MBMPrice']
  },
  {
    title: 'Subtotal 2',
    priceKey: 'subtotal2',
    isDependent: true,
    dependentPricing: 'subtotal1',
    calculatedPriceKeys: ['subtotal1'],
    servicesIncluded: ['speed', 'load', 'unrealisedProductivity']
  },
  {
    title: 'Subtotal Repair Coverage',
    priceKey: 'subtotalRepairCoverage',
    isDependent: false,
    servicesIncluded: ['KCSM_SR_ONDEMAND'],
    calculationFormula: `KCSM_SR_ONDEMAND === 5 ? serviceIncluded changes to KCSM_SR_PKG_INCL : KCSM_SR_ONDEMAND === 6 ? serviceIncluded changes to KCSM_SR_PKG_EXCL`
  },
  {
    title: 'Subtotal Repair Options',
    priceKey: 'subtotalRepairOptions',
    isDependent: true,
    servicesIncluded: ['KCSM_SR_ONDEMAND'],
    dependentPricing: 'subtotalRepairCoverage',
    subFactorsIncluded: [
      'equipmentUsage',
      'technicalPlatform',
      'operationalEnvironment',
      'equipmentAge',
      'speed',
      'load'
    ],
    calculatedPriceKeys: ['subtotalRepairCoverage'],
    calculationFormula: `KCSM_SR_ONDEMAND === 5 ? serviceIncluded changes to KCSM_SR_PKG_INCL : KCSM_SR_ONDEMAND === 6 ? serviceIncluded changes to KCSM_SR_PKG_EXCL`
  },
  {
    title: 'All Equipment Control',
    priceKey: 'allEquipmentControl',
    isDependent: false,
    servicesIncluded: ['KCSM_MOBILE_ELEV_CALL', 'KCSM_APF_ELEV_CALL_API', 'KCSM_APF_SERV_ROBOT_API']
  },
  {
    title: 'All Building User Communication',
    priceKey: 'allBuildingUserCommunication',
    isDependent: false,
    servicesIncluded: ['KCSM_INFOTAINMENT', 'KCSM_SERVICE_COMMUNICATION']
  },
  {
    title: 'All Data and Reports',
    priceKey: 'allDataAndReports',
    isDependent: false,
    servicesIncluded: [
      'KCSM_APF_SERV_INFO_API',
      'KCSM_APF_EQUIP_STAT_API',
      'KCSM_CUST_REPORT',
      'KCSM_CUST_DASHBOARD',
      'KCSM_CUST_PORTAL_SCE',
      'KCSM_CUST_SPECIFIC_NOT'
    ]
  },
  {
    title: 'All Insights',
    priceKey: 'allInsights',
    isDependent: false,
    servicesIncluded: ['KCSM_LCP', 'KCSM_ASSET_MANAGEMENT']
  },
  {
    title: 'Kone Care inspections',
    priceKey: 'KCSM_CARE_INSPECTION',
    isDependent: false,
    servicesIncluded: ['KCSM_CARE_INSPECTION'],
    calculationFormula: `KCSM_CARE_INSPECTION === 3 ? subFactorsIncluded will have numberOfDoors considered`
  },
  {
    title: 'Kone inspections',
    priceKey: 'KCSM_KONE_INSPECTION',
    isDependent: true,
    dependentPricing: 'regularMBMPrice',
    servicesIncluded: ['KCSM_KONE_INSPECTION']
  },
  {
    title: 'Kone inspections frequency',
    priceKey: 'KCSM_KONE_INSP_FREQUENCY',
    isDependent: true,
    dependentPricing: 'KCSM_KONE_INSPECTION',
    servicesIncluded: ['KCSM_KONE_INSP_FREQUENCY']
  },
  {
    title: 'All inspection and testing services',
    priceKey: 'allInspectionAndTestingServices',
    isDependent: true,
    dependentPricing: 'KCSM_CARE_INSPECTION',
    servicesIncluded: [
      'KCSM_INSP_SCE_FREQUENCY',
      'KCSM_INSP_SCE_COST_COVERAGE',
      'KCSM_INSP_SCE_DOCUMENTATION',
      'KCSM_SAFETY_CERTIFICATE',
      'KCSM_INSP_SCE_TEST'
    ],
    calculatedPriceKeys: ['KCSM_KONE_INSP_FREQUENCY'],
    calculationFormula: `KCSM_CARE_INSPECTION and KCSM_KONE_INSPECTION are not considered in the total for elevators`
  },
  {
    title: 'All Additional Services',
    priceKey: 'allAdditionalServices',
    isDependent: false,
    servicesIncluded: [
      'KCSM_TRAINING',
      'KCSM_CLEANING',
      'KCSM_CONTRACT_MANAGEMENT',
      'KCSM_CARE_CO_TRVL_TIME',
      'KCSM_CARE_ZONE',
      'KCSM_CARE_ENV_FEE',
      'KCSM_EXTRA_FEE',
      'KCSM_INSURANCE_COVERAGE',
      'KCO_PC_AVAIL'
    ]
  },
  {
    title: 'Subtotal 3',
    priceKey: 'subtotal3',
    isDependent: false,
    calculatedPriceKeys: [
      'allAdditionalServices',
      'allInspectionAndTestingServices',
      'allInsights',
      'allDataAndReports',
      'allBuildingUserCommunication',
      'allEquipmentControl',
      'subtotalRepairOptions',
      'subtotal2'
    ]
  },
  {
    title: 'Subtotal 4',
    priceKey: 'subtotal4',
    isDependent: true,
    dependentPricing: 'subtotal3',
    calculatedPriceKeys: ['subtotal3'],
    servicesIncluded: [
      'segment',
      'VKBUR',
      'opportunityCategory',
      'fundingSector',
      'willingnessToPay',
      'numberOfEquipment'
    ]
  },
  {
    title: 'KCSM_PROJECT_TYPE',
    priceKey: 'KCSM_PROJECT_TYPE',
    isDependent: true,
    dependentPricing: 'subtotal4',
    servicesIncluded: ['KCSM_PROJECT_TYPE'],
    subFactorsIncluded: ['speed']
  },
  {
    title: 'Grand Total',
    priceKey: 'grandTotal',
    isDependent: true,
    dependentPricing: 'subtotal4',
    calculatedPriceKeys: ['KCSM_PROJECT_TYPE', 'subtotal4'],
    servicesIncluded: [
      'VLAUFK',
      'ZLSCH',
      'AUGRU',
      'KCSM_BILLING_PLAN',
      'ZTERM',
      'KCSM_INV_ATTACH',
      'KCMA_PO_FOR_CALLOUTS'
    ]
  },
  {
    title: 'VAS Total',
    priceKey: 'vasGrandTotal',
    isDependent: false,
    calculatedPriceKeys: [],
    servicesIncluded: [
      'KCSM_SN_IMMEDIATE',
      'KCSM_SN_STANDARD',
      'KCSM_LCP',
      'KCSM_AIR_PURIFIER',
      'KCSM_MOBILE_ELEV_CALL',
      'KCSM_APF_ELEV_CALL_API',
      'KCSM_APF_SERV_ROBOT_API',
      'KCSM_REMOTE_BREAKDOWN',
      'KCSM_REMOTE_RESCUE',
      'KCSM_REMOTE_MAINT',
      'KCSM_INFOTAINMENT',
      'KCSM_APF_EQUIP_STAT_API',
      'KCSM_APF_SERV_INFO_API'
    ]
  }
]

export const escalatorPricingConfig: PricingConfig[] = [
  {
    title: 'Travel Time Price',
    priceKey: 'travelTime',
    isDependent: false,
    servicesIncluded: ['KCSM_DISTANCE_RANGE'],
    subFactorsIncluded: ['baseUsage', 'averageTravelTime', 'hourlyRate'],
    pricingDependents: ['travelTime'],
    pricingFactors: ['hourlyRate', 'averageTravelTime', 'KCSM_DISTANCE_RANGE'],
    calculationFormula: `hourlyRate * averageTravelTime * (1+KCSM_DISTANCE_RANGE)`
  },
  {
    title: 'Initial MBM Rise Price',
    priceKey: 'initMBMRisePrice',
    isDependent: false,
    servicesIncluded: ['KCSM_CARE_MBM'],
    subFactorsIncluded: ['rise'],
    calculationFormula: `equipmentType === 'autowalk' ? length : rise`
  },
  {
    title: 'Regular MBM Price',
    priceKey: 'regularMBMPrice',
    dependentPricing: 'initMBMRisePrice',
    isDependent: true,
    servicesIncluded: ['KCSM_CARE_MBM'],
    subFactorsIncluded: [
      'numberOfDrives',
      'technicalPlatform',
      'equipmentAge',
      'operationalEnvironment',
      'escalatorOperationalEnvironment'
    ],
    calculatedPriceKeys: ['initMBMRisePrice', 'travelTime'],
    calculationFormula: `sum of (initMBMRisePrice + travelTime + numberOfDrives + technicalPlatform + equipmentAge + operationalEnvironment + operationalEnvironmentCondition)`
  },
  {
    title: 'MBM Price',
    priceKey: 'MBMPrice',
    dependentPricing: 'regularMBMPrice',
    isDependent: true,
    servicesIncluded: [
      'KCSM_CARE_VISIT',
      'KCSM_VISIT_FREQUENCY_YEARLY',
      'KCSM_AGREED_CUST_VISITS_YEARLY',
      'KCSM_SN_STANDARD',
      'KCSM_MAINT_HOURS',
      'KCSM_MAINT_PRE_BOOK',
      'KCSM_PREFERRED_TECHNICIAN',
      'KCSM_PRESENCE'
    ],
    calculatedPriceKeys: ['regularMBMPrice'],
    calculationFormula: `sum of (regularMBMPrice + KCSM_CARE_VISIT + KCSM_VISIT_FREQUENCY_YEARLY + KCSM_AGREED_CUST_VISITS_YEARLY + KCSM_SN_STANDARD + KCSM_MAINT_HOURS + KCSM_MAINT_PRE_BOOK + KCSM_PREFERRED_TECHNICIAN + KCSM_PRESENCE)`
  },
  {
    title: 'Initial Call Out Rise Price',
    priceKey: 'initCORisePrice',
    isDependent: false,
    servicesIncluded: ['KCSM_CO'],
    subFactorsIncluded: ['rise'],
    calculationFormula: `KCSM_CO === 0 ? serviceIncluded will change to KCSM_NBR_CO_YEAR and relevant factors are considered `
  },
  {
    title: 'Factor Call Out Price',
    priceKey: 'factorCOPrice',
    isDependent: true,
    dependentPricing: 'initCORisePrice',
    servicesIncluded: ['KCSM_CO'],
    subFactorsIncluded: [
      'numberOfDrives',
      'technicalPlatform',
      'equipmentAge',
      'operationalEnvironment',
      'escalatorOperationalEnvironment'
    ],
    calculationFormula: `KCSM_CO === 0 ? serviceIncluded will change to KCSM_NBR_CO_YEAR and relevant factors are considered `
  },
  {
    title: 'Regular Call Out Price',
    priceKey: 'regularCOPrice',
    isDependent: true,
    dependentPricing: 'initCORisePrice',
    servicesIncluded: ['KCSM_CCC'],
    calculatedPriceKeys: ['initCORisePrice', 'factorCOPrice', 'travelTime']
  },
  {
    title: 'Misuse Price',
    priceKey: 'misusePrice',
    isDependent: true,
    dependentPricing: 'regularCOPrice',
    servicesIncluded: ['KCSM_CARE_MISUSE'],
    calculatedPriceKeys: ['travelTime']
  },
  {
    title: 'Call Out Price',
    priceKey: 'COPriceOne',
    isDependent: true,
    dependentPricing: 'regularCOPrice',
    servicesIncluded: ['KCSM_SERVICE_HOURS', 'KCSM_CO_HOURS']
  },
  {
    title: 'Call Out Subtotal',
    priceKey: 'callOutSubTotalOne',
    isDependent: false,
    calculatedPriceKeys: ['regularCOPrice', 'misusePrice', 'COPriceOne']
  },
  {
    title: 'SN Immediate Price',
    priceKey: 'snImmediatePrice',
    isDependent: false,
    servicesIncluded: ['KCSM_SN_IMMEDIATE']
  },
  {
    title: 'All Call Out Price',
    priceKey: 'allCallOutPrice',
    isDependent: true,
    dependentPricing: 'callOutSubTotalOne',
    calculatedPriceKeys: ['regularCOPrice', 'COPriceOne', 'misusePrice', 'snImmediatePrice'],
    servicesIncluded: ['KCSM_REMOTE_BREAKDOWN', 'KCO_PC_RESP', 'KCO_PC_RESP_A', 'KCSM_CARE_CALL_OUT_FEE']
  },
  {
    title: 'Subtotal 1',
    priceKey: 'subtotal1',
    isDependent: false,
    servicesIncluded: [],
    calculatedPriceKeys: ['allCallOutPrice', 'MBMPrice']
  },
  {
    title: 'Subtotal 2',
    priceKey: 'subtotal2',
    isDependent: true,
    dependentPricing: 'subtotal1',
    calculatedPriceKeys: ['subtotal1'],
    servicesIncluded: ['unrealisedProductivity']
  },
  {
    title: 'Subtotal Repair Coverage',
    priceKey: 'subtotalRepairCoverage',
    isDependent: false,
    servicesIncluded: ['KCSM_SR_ONDEMAND'],
    calculationFormula: `KCSM_SR_ONDEMAND === 5 ? serviceIncluded changes to KCSM_SR_PKG_INCL : KCSM_SR_ONDEMAND === 6 ? serviceIncluded changes to KCSM_SR_PKG_EXCL`
  },
  {
    title: 'Subtotal Repair Options',
    priceKey: 'subtotalRepairOptions',
    isDependent: true,
    servicesIncluded: ['KCSM_SR_ONDEMAND'],
    dependentPricing: 'subtotalRepairCoverage',
    subFactorsIncluded: [
      'numberOfDrives',
      'technicalPlatform',
      'equipmentAge',
      'operationalEnvironment',
      'escalatorOperationalEnvironment'
    ],
    calculatedPriceKeys: ['subtotalRepairCoverage']
  },
  {
    title: 'All Customer Service',
    priceKey: 'allCustomerService',
    isDependent: false,
    servicesIncluded: [
      'KCSM_CSC',
      'KCSM_CARE_ONLINE',
      'KCSM_CUST_NOT_MOBILE',
      'KCSM_CUST_NOTIFY_YN',
      'KCSM_DISRUPTION_INCIDENT_NOT',
      'KCSM_SCE_VISIT_NOT'
    ]
  },
  {
    title: 'All Data and Reports',
    priceKey: 'allDataAndReports',
    isDependent: false,
    servicesIncluded: [
      'KCSM_APF_SERV_INFO_API',
      'KCSM_APF_EQUIP_STAT_API',
      'KCSM_CUST_REPORT',
      'KCSM_CUST_DASHBOARD',
      'KCSM_CUST_PORTAL_SCE',
      'KCSM_CUST_SPECIFIC_NOT'
    ]
  },
  {
    title: 'All Insights',
    priceKey: 'allInsights',
    isDependent: false,
    servicesIncluded: ['KCSM_ASSET_MANAGEMENT']
  },
  {
    title: 'Kone Care inspections',
    priceKey: 'KCSM_CARE_INSPECTION',
    isDependent: false,
    servicesIncluded: ['KCSM_CARE_INSPECTION']
  },
  {
    title: 'Kone inspections',
    priceKey: 'KCSM_KONE_INSPECTION',
    isDependent: true,
    dependentPricing: 'regularMBMPrice',
    servicesIncluded: ['KCSM_KONE_INSPECTION']
  },
  {
    title: 'Kone Care Inspections Frequency',
    priceKey: 'KCSM_KONE_INSP_FREQUENCY',
    isDependent: true,
    dependentPricing: 'KCSM_KONE_INSPECTION',
    servicesIncluded: ['KCSM_KONE_INSP_FREQUENCY']
  },
  {
    title: 'All inspection and testing services',
    priceKey: 'allInspectionAndTestingServices',
    isDependent: true,
    dependentPricing: 'KCSM_CARE_INSPECTION',
    servicesIncluded: [
      'KCSM_INSP_SCE_FREQUENCY',
      'KCSM_INSP_SCE_COST_COVERAGE',
      'KCSM_INSP_SCE_DOCUMENTATION',
      'KCSM_SAFETY_CERTIFICATE',
      'KCSM_INSP_SCE_TEST'
    ],
    calculatedPriceKeys: ['KCSM_CARE_INSPECTION', 'KCSM_KONE_INSPECTION', 'KCSM_KONE_INSP_FREQUENCY'],
    calculationFormula: `KCSM_CARE_INSPECTION and KCSM_KONE_INSPECTION are considered in the total for escalators`
  },
  {
    title: 'All Additional Services',
    priceKey: 'allAdditionalServices',
    isDependent: false,
    servicesIncluded: [
      'KCSM_TRAINING',
      'KCSM_CLEANING',
      'KCSM_CONTRACT_MANAGEMENT',
      'KCSM_CARE_CO_TRVL_TIME',
      'KCSM_CARE_ZONE',
      'KCSM_CARE_ENV_FEE',
      'KCSM_EXTRA_FEE',
      'KCSM_INSURANCE_COVERAGE',
      'KCO_PC_AVAIL'
    ]
  },
  {
    title: 'Subtotal 3',
    priceKey: 'subtotal3',
    isDependent: false,
    calculatedPriceKeys: [
      'allAdditionalServices',
      'allInspectionAndTestingServices',
      'allInsights',
      'allDataAndReports',
      'allBuildingUserCommunication',
      'allEquipmentControl',
      'subtotalRepairOptions',
      'subtotal2'
    ]
  },
  {
    title: 'Subtotal 4',
    priceKey: 'subtotal4',
    isDependent: true,
    dependentPricing: 'subtotal3',
    calculatedPriceKeys: ['subtotal3'],
    servicesIncluded: [
      'segment',
      'VKBUR',
      'opportunityCategory',
      'fundingSector',
      'willingnessToPay',
      'numberOfEquipment'
    ]
  },
  {
    title: 'KCSM_PROJECT_TYPE',
    priceKey: 'KCSM_PROJECT_TYPE',
    isDependent: true,
    dependentPricing: 'subtotal4',
    servicesIncluded: ['KCSM_PROJECT_TYPE'],
    subFactorsIncluded: ['speed']
  },
  {
    title: 'Grand Total',
    priceKey: 'grandTotal',
    isDependent: true,
    dependentPricing: 'subtotal4',
    calculatedPriceKeys: ['KCSM_PROJECT_TYPE', 'subtotal4'],
    servicesIncluded: [
      'VLAUFK',
      'ZLSCH',
      'AUGRU',
      'KCSM_BILLING_PLAN',
      'ZTERM',
      'KCSM_INV_ATTACH',
      'KCMA_PO_FOR_CALLOUTS'
    ]
  },
  {
    title: 'VAS Total',
    priceKey: 'vasGrandTotal',
    isDependent: false,
    calculatedPriceKeys: [],
    servicesIncluded: ['KCSM_SN_IMMEDIATE', 'KCSM_SN_STANDARD', 'KCSM_APF_EQUIP_STAT_API', 'KCSM_APF_SERV_INFO_API']
  }
]
